import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { DayPicker } from "react-day-picker";
import { Calendar, ChevronLeft, ChevronRight } from "@untitledui/icons";
import { cx } from "@/utils/cx";

interface DatePickerProps {
  value: string; // ISO date string YYYY-MM-DD
  onChange: (value: string) => void;
  label?: string;
}

function toDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toIso(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplay(iso: string) {
  const date = toDate(iso);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function DatePicker({ value, onChange, label }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(toIso(date));
      setOpen(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-text-tertiary whitespace-nowrap">{label}</span>}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cx(
              "inline-flex items-center gap-2 rounded-lg border border-border-secondary bg-bg-primary px-3 h-10 text-sm text-text-primary",
              "hover:bg-bg-secondary_hover transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-fg-brand-primary focus:ring-offset-1",
            )}
          >
            <Calendar className="size-4 text-text-tertiary" />
            <span>{formatDisplay(value)}</span>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            sideOffset={8}
            align="start"
            className="z-50 rounded-xl border border-border-secondary bg-bg-primary p-3 shadow-lg animate-in fade-in-0 zoom-in-95"
          >
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={handleSelect}
              defaultMonth={selected}
              showOutsideDays
              classNames={{
                root: "text-sm",
                months: "flex flex-col",
                month_caption: "flex items-center justify-center py-1 relative",
                caption_label: "text-sm font-semibold text-text-primary",
                nav: "flex items-center justify-between absolute inset-x-0",
                button_previous: cx(
                  "size-7 inline-flex items-center justify-center rounded-md",
                  "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary transition-colors",
                ),
                button_next: cx(
                  "size-7 inline-flex items-center justify-center rounded-md",
                  "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary transition-colors",
                ),
                month_grid: "mt-2",
                weekdays: "flex",
                weekday: "w-9 text-center text-xs font-medium text-text-tertiary py-1",
                week: "flex",
                day: "p-0",
                day_button: cx(
                  "size-9 inline-flex items-center justify-center rounded-md text-sm",
                  "text-text-primary hover:bg-bg-secondary_hover transition-colors",
                  "focus:outline-none focus:ring-2 focus:ring-fg-brand-primary",
                ),
                selected: "!bg-fg-brand-primary !text-white rounded-md hover:!bg-fg-brand-primary",
                today: "font-bold text-fg-brand-primary",
                outside: "text-text-disabled opacity-50",
                disabled: "text-text-disabled cursor-not-allowed",
              }}
              components={{
                Chevron: ({ orientation }) =>
                  orientation === "left" ? (
                    <ChevronLeft className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  ),
              }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
